/**
 * One-time migrations that flip a previously opt-in setting to default-on.
 *
 * `useLocalStorage` persists its value from a mount effect, unconditionally —
 * so every browser that has ever opened a workspace already holds an explicit
 * entry for these keys. Raising the declared default therefore only reaches
 * brand-new browsers; existing users would keep the old `false` forever, and
 * the change would look like a no-op to everyone who actually asked for it.
 *
 * Each migration below rewrites the stored entry exactly once and then records
 * itself as applied, so a later deliberate "off" is never overwritten again.
 *
 * Note the unavoidable cost: because the mount effect writes `false` whether or
 * not the user ever touched the toggle, "never opened the panel" and "opened it
 * and turned this off" are indistinguishable in storage. The flip therefore
 * also re-enables the setting for the handful of users who had turned it off on
 * purpose. It is one-time — their next "off" sticks.
 */
import { CANVAS_PERFORMANCE_SETTINGS_STORAGE_KEY } from './canvasPerformanceSettings'
import { TASK_COMPLETION_CHIME_STORAGE_KEY } from './taskCompletionChime'

export const SNAP_TO_GRID_DEFAULT_ON_MIGRATION_KEY =
  'dreamwright_snap_to_grid_default_on_applied'
export const TASK_COMPLETION_CHIME_DEFAULT_ON_MIGRATION_KEY =
  'dreamwright_task_completion_chime_default_on_applied'

/**
 * Claims a migration: returns true the first time it is called for `flagKey`
 * on this browser, false on every later call. Marks the flag before the caller
 * does any work, so a throwing write cannot leave the migration re-running on
 * every page load.
 */
function claimMigration(flagKey: string): boolean {
  if (typeof window === 'undefined') return false
  try {
    if (window.localStorage.getItem(flagKey) !== null) return false
    window.localStorage.setItem(flagKey, JSON.stringify(true))
    return true
  } catch {
    // Storage unavailable (private mode, quota). Nothing is persisted in that
    // browser anyway, so the raised default already applies for the session.
    return false
  }
}

/**
 * Where a setting's value came from on this page load.
 *
 * `code_default` is the one that evidences this change: nothing was stored, so
 * the raised declared default is what the user gets. Without it a trace showing
 * `enabled: true` is indistinguishable from a stale stored preference or a user
 * toggle, which is exactly the question a reviewer needs answered.
 */
export type DefaultOnSource = 'code_default' | 'local_storage' | 'default_on_migration'

export interface DefaultOnResolution {
  /** Value in storage before this ran; `null` means the key was absent. */
  storedValueBefore: boolean | null
  effectiveValue: boolean
  source: DefaultOnSource
  /**
   * Whether this page load actually REWROTE a stored value.
   *
   * Not the same as "the migration slot was claimed". A fresh browser claims
   * the slot too — there was simply nothing to rewrite — and reporting `true`
   * there would contradict `source: 'code_default'` on the same record and make
   * this field useless for spotting real migrations.
   */
  migrationApplied: boolean
}

export interface DefaultOnMigrationOutcome {
  snapToGrid: DefaultOnResolution
  taskCompletionChime: DefaultOnResolution
}

/** Reads the stored `snapToGrid`, or `null` when absent/unreadable. */
function readStoredSnapToGrid(): boolean | null {
  try {
    const raw = window.localStorage.getItem(CANVAS_PERFORMANCE_SETTINGS_STORAGE_KEY)
    if (raw === null) return null
    const stored = JSON.parse(raw)
    if (stored === null || typeof stored !== 'object' || Array.isArray(stored)) return null
    return typeof stored.snapToGrid === 'boolean' ? stored.snapToGrid : null
  } catch {
    return null
  }
}

/** Reads the stored chime flag, or `null` when absent/unreadable. */
function readStoredTaskCompletionChime(): boolean | null {
  try {
    const raw = window.localStorage.getItem(TASK_COMPLETION_CHIME_STORAGE_KEY)
    if (raw === null) return null
    const stored = JSON.parse(raw)
    return typeof stored === 'boolean' ? stored : null
  } catch {
    return null
  }
}

/**
 * Turns `snapToGrid` on inside the stored canvas performance settings bag.
 * A missing bag needs no migration — the raised default covers it.
 */
function migrateSnapToGridDefaultOn(): void {
  try {
    const raw = window.localStorage.getItem(CANVAS_PERFORMANCE_SETTINGS_STORAGE_KEY)
    if (raw === null) return
    const stored = JSON.parse(raw)
    if (stored === null || typeof stored !== 'object' || Array.isArray(stored)) return
    window.localStorage.setItem(
      CANVAS_PERFORMANCE_SETTINGS_STORAGE_KEY,
      JSON.stringify({ ...stored, snapToGrid: true }),
    )
  } catch {
    // Unparseable or unwritable: `normalizeCanvasPerformanceSettings` falls back
    // to the raised default, which is the value this migration wanted anyway.
  }
}

/** Turns the completion chime on for a browser that already stored `false`. */
function migrateTaskCompletionChimeDefaultOn(): void {
  try {
    if (window.localStorage.getItem(TASK_COMPLETION_CHIME_STORAGE_KEY) === null) return
    window.localStorage.setItem(TASK_COMPLETION_CHIME_STORAGE_KEY, JSON.stringify(true))
  } catch {
    // Same as above — the raised default is the intended value.
  }
}

function resolve(
  storedValueBefore: boolean | null,
  migrationClaimed: boolean,
  codeDefault: boolean,
): DefaultOnResolution {
  // Nothing stored: the raised declared default is what the user gets. The slot
  // was claimed, but no value was rewritten, so `migrationApplied` stays false.
  if (storedValueBefore === null) {
    return {
      storedValueBefore,
      effectiveValue: codeDefault,
      source: 'code_default',
      migrationApplied: false,
    }
  }
  if (migrationClaimed) {
    return {
      storedValueBefore,
      effectiveValue: true,
      source: 'default_on_migration',
      migrationApplied: true,
    }
  }
  return {
    storedValueBefore,
    effectiveValue: storedValueBefore,
    source: 'local_storage',
    migrationApplied: false,
  }
}

/**
 * First resolution of this page load, kept so every later caller sees what
 * ACTUALLY happened rather than the post-migration state.
 *
 * This is load-bearing, not an optimization. React StrictMode double-invokes
 * the `useMemo` that calls this, and the canvas remounts on tab switches.
 * Without the cache the second call sees the flag already set and the value
 * already rewritten, so a migrated user gets reported as
 * `source: 'local_storage'` — misattributing the migration as a pre-existing
 * preference, which is precisely the case the reporting exists to evidence.
 */
let cachedOutcome: DefaultOnMigrationOutcome | null = null

/** Test-only: clears the once-per-page-load resolution cache. */
export function resetDefaultOnMigrationsForTests(): void {
  cachedOutcome = null
}

/**
 * Runs every pending default-on migration and reports how each setting's value
 * was arrived at. Idempotent, and safe to call during render: it must run
 * BEFORE the `useLocalStorage` calls that read these keys, because those hooks
 * snapshot storage in their state initializer.
 */
export function applyDefaultOnMigrations(): DefaultOnMigrationOutcome {
  if (cachedOutcome !== null) return cachedOutcome
  const snapStoredBefore = typeof window === 'undefined' ? null : readStoredSnapToGrid()
  const snapMigrated = claimMigration(SNAP_TO_GRID_DEFAULT_ON_MIGRATION_KEY)
  if (snapMigrated) {
    migrateSnapToGridDefaultOn()
  }

  const chimeStoredBefore = typeof window === 'undefined' ? null : readStoredTaskCompletionChime()
  const chimeMigrated = claimMigration(TASK_COMPLETION_CHIME_DEFAULT_ON_MIGRATION_KEY)
  if (chimeMigrated) {
    migrateTaskCompletionChimeDefaultOn()
  }

  cachedOutcome = {
    snapToGrid: resolve(snapStoredBefore, snapMigrated, true),
    taskCompletionChime: resolve(chimeStoredBefore, chimeMigrated, true),
  }
  return cachedOutcome
}
